import {
  AnnualPlanRevenueData,
  AnnualPlanServices,
  AnnualPlanTotals,
  CardDetails,
  OfficeActivePlan,
  OfficeAnnualPlan,
  Subscription,
  SubscriptionType,
} from "../models/annual-plan.model";
import { UserAnnualPlan } from "../models/user-annual-plan.model";
import { ApiPatchOp, apiService } from "./api.service";
export class AnnualPlanService {
  private static BASE_PATH = "/api/v1/offices";
  private static BASE_PATH_NEW = "/api/v1";

  static async getOfficeAnnualPlans(officeId: string): Promise<OfficeAnnualPlan[]> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/annual-plans`);
    if (!response.success) return [];

    return response.data;
  }

  static async getSubscriptions(officeId: string, frequency: SubscriptionType): Promise<Subscription[]> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/subscriptions/${frequency}`);
    if (!response.success) return [];

    return response.data;
  }

  static async updateOfficePlan(officeId: string, body: OfficeActivePlan): Promise<OfficeActivePlan | undefined> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/annual-plans`, body);
    if (!response.success) return undefined;

    return response.data;
  }

  static async updateOfficeAnnualPlanServices(officeId: string, body: AnnualPlanServices[]): Promise<boolean> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/annual-plans/services`, body);
    if (!response.success) return false;

    return true;
  }

  static async getOfficeAnnualPlanServices(officeId: string): Promise<AnnualPlanServices[]> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/annual-plans/services`);
    if (!response.success) return [];

    return response.data;
  }

  static async getSubscriptionPaymentIntent(officeAnnualPlanId: string, userId: string, officeId: string, additionalMembers?: string): Promise<any> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/${userId}/subscribe`, {
      officeAnnualPlanId,
      additionalMembers,
    });
    if (!response.success) return undefined;

    return response.data;
  }

  static async getUpdateSubscriptionPaymentIntent(
    existingAnnualPlanId: string,
    userId: string,
    officeId: string,
    additionalMembers?: string,
    newAnnualPlanId?: string
  ): Promise<any> {
    const response = await apiService.put(`${this.BASE_PATH}/${officeId}/users/${userId}/subscribe-update`, {
      existingAnnualPlanId,
      additionalMembers,
      newAnnualPlanId,
    });
    if (!response.success) return response;

    return response.data;
  }

  static async getValidateSubscribeUpdate(
    existingAnnualPlanId: string,
    userId: string,
    officeId: string,
    additionalMembers?: string,
    newAnnualPlanId?: string
  ): Promise<any> {
    const response = await apiService.put(`${this.BASE_PATH}/${officeId}/users/${userId}/validate-subscribe-update`, {
      existingAnnualPlanId,
      newAnnualPlanId,
    });
    if (!response.success) return response;

    return response.data;
  }
  static async getPaymentUpgrade(officeId: string, userId: string, proratedAmount?: number, proratedDate?: number): Promise<any> {
    const response = await apiService.put(`${this.BASE_PATH}/${officeId}/users/${userId}/confirm-upgrade`, {
      proratedAmount,
      proratedDate,
    });
    if (!response.success) return response;

    return response.data;
  }

  static async completePatientSubscription(
    officeAnnualPlanId: string,
    userId: string,
    officeId: string,
    stripeSubscriptionId: string,
    paymentId: string
  ): Promise<any> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/${userId}/subscribe-complete`, {
      officeAnnualPlanId,
      userId,
      stripeSubscriptionId,
      stripePaymentMethodId: paymentId,
    });
    if (!response.success) return undefined;

    return response.data;
  }
  static async getReceiptData(userAnnualPlanId: string, userId: string, officeId: string): Promise<CardDetails | undefined> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/users/${userId}/annual-plans/${userAnnualPlanId}/invoice`);
    if (!response.success) return undefined;

    return response.data;
  }

  static async uploadStripeReceipt(userAnnualPlanId: string, userId: string, fileID: string): Promise<any> {
    const response = await apiService.post(`${this.BASE_PATH_NEW}/users/${userId}/annual-plan/${userAnnualPlanId}/files/${fileID}`);
    if (!response.success) return false;

    return true;
  }

  static async cancelAnnualPlan(userAnnualPlanId: string, userId: string, officeId: string, reason?: string): Promise<any> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/${userId}/annual-plans/${userAnnualPlanId}/cancel`, {
      cancelledReason: reason,
    });
    if (!response.success) return false;

    return true;
  }

  static async getCardDetails(userAnnualPlanId: string, userId: string, officeId: string): Promise<CardDetails | undefined> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/users/${userId}/annual-plans/${userAnnualPlanId}/payment-method`);
    if (!response.success) return undefined;

    return response.data;
  }

  static async updateFamilyMembers(userAnnualPlanId: string, userId: string, officeId: string, family: string): Promise<UserAnnualPlan | undefined> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/users/${userId}/annual-plans/${userAnnualPlanId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/additionalMembers",
        value: family,
      },
    ]);
    if (!response.success) return undefined;

    return response.data;
  }

  static async setupIntent(userId: string, officeId: string): Promise<string> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/${userId}/setup-intent`);
    if (!response.success) return "";

    return response.data;
  }

  static async updatePaymentMethod(userId: string, officeId: string, paymentMethodId: string): Promise<any> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/users/${userId}/payment-methods`, {
      paymentMethodId,
    });
    if (!response.success) return undefined;

    return response.data;
  }

  static async getAnnualPlanNotifications(officeId: string, archived = false): Promise<any | undefined> {
    const response = await apiService.post(`${this.BASE_PATH}/${officeId}/annual-plans/notifications/search`, {
      archived,
    });
    if (!response.success) return undefined;

    return response.data;
  }

  static async archiveAnnualPlanNotification(officeId: string, notificationId: string, archive: boolean): Promise<any | undefined> {
    const response = await apiService.patch(`${this.BASE_PATH}/${officeId}/annual-plans/notifications/${notificationId}`, [
      {
        op: ApiPatchOp.Replace,
        path: "/archived",
        value: !archive,
      },
    ]);
    if (!response.success) return undefined;

    return response.data;
  }

  static async getOfficeAnnualPlanTotals(officeId: string): Promise<AnnualPlanTotals | undefined> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/annual-plan-totals`);
    if (!response.success) return undefined;
    return response.data;
  }

  static async getOfficeAnnualPlanRevenueByPlan(officeId: string): Promise<AnnualPlanRevenueData | undefined> {
    const response = await apiService.get(`${this.BASE_PATH}/${officeId}/annual-plan-revenue-by-plan`);
    if (!response.success) return undefined;
    return response.data;
  }
}
